import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

function fmtErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail?.msg || detail);
}

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate(user.role === "founder_admin" ? "/admin" : "/dashboard");
  }, [user, loading, navigate]);

  const signInGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "forgot") {
      if (!form.email) return toast.error("Enter your email");
    } else if (!form.email || !form.password) {
      return toast.error("Email and password are required");
    }
    setBusy(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success("If that email exists, a reset link is on its way.");
        setMode("login");
        return;
      }
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(path, payload);
      setUser(data.user);
      navigate(data.user.role === "founder_admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-950 to-indigo-950 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-2xl">
        <button onClick={() => navigate("/")} className="text-sm text-slate-500 flex items-center gap-1 mb-6 hover:text-slate-800"><ArrowLeft className="h-4 w-4" /> Home</button>
        <div className="flex items-center gap-2 mb-1">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center"><span className="text-white font-heading font-bold text-lg">A</span></div>
          <span className="font-heading font-bold text-lg text-slate-900 leading-tight">Make My Business AI Ready <span className="text-indigo-600">(AgenticReady)</span></span>
        </div>
        <p className="text-xs text-indigo-500 mb-5">Get Found. Get Chosen. Get Paid.</p>

        <div className="flex gap-1 p-1 bg-slate-100 rounded-full mb-5">
          {["login", "register"].map((m) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`auth-tab-${m}`}
              className={`flex-1 py-2 rounded-full text-sm font-medium capitalize transition-colors ${mode === m ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>
              {m === "login" ? "Log in" : "Sign up"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <Input data-testid="auth-name" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12" />
          )}
          <Input data-testid="auth-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12" />
          {mode !== "forgot" && (
            <Input data-testid="auth-password" type="password" placeholder="Password (min 8 characters)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-12" />
          )}
          <Button type="submit" disabled={busy} data-testid="auth-submit" className="w-full h-12 rounded-full bg-indigo-600 hover:bg-indigo-700 font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Log in" : mode === "register" ? "Create account" : "Send reset link"}
          </Button>
          {mode === "login" && (
            <button type="button" onClick={() => setMode("forgot")} data-testid="forgot-link" className="w-full text-center text-sm text-indigo-600 hover:underline">Forgot password?</button>
          )}
          {mode === "forgot" && (
            <button type="button" onClick={() => setMode("login")} className="w-full text-center text-sm text-slate-500 hover:underline">Back to log in</button>
          )}
        </form>

        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-slate-200" /><span className="text-xs text-slate-400">or</span><div className="flex-1 h-px bg-slate-200" />
        </div>
        <Button onClick={signInGoogle} data-testid="google-login-btn" variant="outline" className="w-full h-12 rounded-full font-semibold border-slate-300">
          Continue with Google
        </Button>
        <p className="text-xs text-slate-400 mt-4 text-center">No-touch onboarding. No sales call required.</p>
      </div>
    </div>
  );
}
